import { LANGUAGES } from '../i18n/languages';
import { TRANSLATIONS } from '../i18n/translations';
import type { Language } from '../i18n/language';
import type { TranslationKey } from '../i18n/translation';
import type { PageMetadata } from './page-metadata';

/**
 * A public page that exists at a fixed path in both languages.
 *
 * **This list is the prerenderer's input and the pages' own metadata, which is why it is one list.**
 * A page whose title is computed in its component and again in the build is a page whose two heads
 * drift; a page missing from here is one the build silently does not write.
 *
 * **Articles are not here and cannot be.** An article's path is a per-language slug written months
 * after the build, so its HTML is produced when it is published rather than when the bundle is —
 * the other half of the same decision.
 */
export interface StaticPage {
    /** Appended after the language prefix. Empty for the home page. */
    readonly path: string;
    readonly title: TranslationKey;
    readonly description: TranslationKey;

    /**
     * A class the page puts on `<html>`, written into the prerendered file as well.
     *
     * There is exactly one, and it is `book-root`: the booking wizard sets a root font size and
     * every length in its stylesheet is a `rem`, so markup served without the class is laid out at
     * the wrong scale until hydration puts it there. See `useRootClass`.
     */
    readonly rootClass?: string;
}

export const STATIC_PAGES: readonly StaticPage[] = [
    { path: '', title: 'site.page.title', description: 'site.page.desc' },
    { path: 'policy', title: 'policy.page.title', description: 'policy.page.desc' },
    { path: 'book', title: 'booking.page.title', description: 'booking.page.desc', rootClass: 'book-root' },
    { path: 'blog', title: 'blog.page.title', description: 'blog.page.desc' }
];

/**
 * What a static page says about itself in one language.
 *
 * The alternates are declared for **every** language, which is true here and is exactly what is not
 * true of an article: these pages exist at the same path in both, so the pair of `hreflang` links is
 * a fact rather than a claim. An article publishes per language and declares only what exists.
 */
export function staticPageMetadata(page: StaticPage, language: Language): PageMetadata {
    const strings = TRANSLATIONS[language];
    const pathFor = (code: Language) => (page.path === '' ? `/${code}` : `/${code}/${page.path}`);

    return {
        title: strings[page.title],
        description: strings[page.description],
        canonicalPath: pathFor(language),
        alternatePaths: Object.fromEntries(LANGUAGES.map((code) => [code, pathFor(code)]))
    };
}

/** Every page in every language — what the build writes, one file each. */
export function allStaticPages(): { page: StaticPage; language: Language; metadata: PageMetadata }[] {
    return STATIC_PAGES.flatMap((page) =>
        LANGUAGES.map((language) => ({ page, language, metadata: staticPageMetadata(page, language) }))
    );
}
